import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Edit, 
  Target, 
  Building2, 
  Users, 
  Calendar,
  DollarSign,
  MessageSquare,
  Rocket,
  Save,
  Download,
  Mail
} from 'lucide-react';
import { OnboardingFormData } from '../../../types/onboarding';

interface ReviewStepProps {
  data: OnboardingFormData;
  onEdit: (step: string) => void;
  onSaveDraft: () => void;
  onLaunch: () => void;
  onBack: () => void;
  className?: string;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  data,
  onEdit,
  onSaveDraft,
  onLaunch,
  onBack,
  className = '',
}) => {
  const [isLaunching, setIsLaunching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleLaunch = async () => {
    setIsLaunching(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    onLaunch();
    setIsLaunching(false);
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    onSaveDraft();
    setIsSaving(false);
  };

  const sections = [
    {
      id: 'company',
      title: 'Company Information',
      icon: Building2,
      color: 'primary',
      items: [
        { label: 'Company Name', value: data.companyInformation.companyName },
        { label: 'Website', value: data.companyInformation.website || 'Not provided' },
        { label: 'Industry', value: data.companyInformation.industries.join(', ') },
        { label: 'Company Size', value: data.companyInformation.companySize },
        { label: 'Product Description', value: data.companyInformation.productDescription, truncate: true },
        { label: 'Value Proposition', value: data.companyInformation.valueProposition, truncate: true },
      ]
    },
    {
      id: 'targeting',
      title: 'Target Audience',
      icon: Target,
      color: 'secondary',
      items: [
        { label: 'Target Description', value: data.targetCriteria.targetDescription, truncate: true },
        { label: 'Company Sizes', value: data.targetCriteria.companySize.join(', ') },
        { label: 'Locations', value: data.targetCriteria.location.join(', ') },
        { label: 'Industries', value: data.targetCriteria.industries.join(', ') },
        { label: 'Job Titles', value: data.targetCriteria.jobTitles.join(', ') },
        { label: 'Departments', value: data.targetCriteria.departments.join(', ') },
        { label: 'Pain Points', value: data.targetCriteria.painPoints.join(', ') },
        { label: 'Buying Signals', value: data.targetCriteria.buyingSignals.join(', ') },
      ]
    },
    {
      id: 'goals',
      title: 'Campaign Goals',
      icon: Users,
      color: 'warning',
      items: [
        { label: 'Primary Objective', value: data.campaignGoals.primaryObjective },
        { label: 'Timeline', value: data.campaignGoals.timeline },
        { label: 'Volume Expectations', value: data.campaignGoals.volumeExpectations },
        { label: 'Budget', value: data.campaignGoals.budget },
        { label: 'Outreach Channels', value: data.campaignGoals.outreachChannels.join(', ') },
        { label: 'Additional Notes', value: data.campaignGoals.additionalNotes || 'None' },
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'primary':
        return {
          bg: 'bg-primary-50',
          border: 'border-primary-200',
          text: 'text-primary-600',
          button: 'text-primary-600 hover:text-primary-700'
        };
      case 'secondary':
        return {
          bg: 'bg-secondary-50',
          border: 'border-secondary-200',
          text: 'text-secondary-600',
          button: 'text-secondary-600 hover:text-secondary-700'
        };
      case 'warning':
        return {
          bg: 'bg-warning-50',
          border: 'border-warning-200',
          text: 'text-warning-600',
          button: 'text-warning-600 hover:text-warning-700'
        };
      default:
        return {
          bg: 'bg-neutral-50',
          border: 'border-neutral-200',
          text: 'text-neutral-600',
          button: 'text-neutral-600 hover:text-neutral-700'
        };
    }
  };

  return (
    <div className={`max-w-4xl mx-auto px-6 py-8 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex p-3 bg-success-50 rounded-full mb-4">
            <CheckCircle className="w-6 h-6 text-success-500" />
          </div>
          <h2 className="text-3xl font-bold text-neutral-900 mb-2">
            Review Your Campaign Setup
          </h2>
          <p className="text-neutral-600">
            Please review all information before launching your intelligent outreach campaign
          </p>
        </div>

        {/* Campaign Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6 mb-8"
        >
          <h3 className="text-xl font-semibold text-neutral-900 mb-4">Campaign Overview</h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <Building2 className="w-8 h-8 text-primary-500 mx-auto mb-2" />
              <div className="text-sm text-neutral-600">Company</div>
              <div className="font-semibold text-neutral-900">{data.companyInformation.companyName}</div>
            </div>
            <div className="text-center">
              <Target className="w-8 h-8 text-secondary-500 mx-auto mb-2" />
              <div className="text-sm text-neutral-600">Industry</div>
              <div className="font-semibold text-neutral-900">{data.companyInformation.industries.join(', ')}</div>
            </div>
            <div className="text-center">
              <Users className="w-8 h-8 text-warning-500 mx-auto mb-2" />
              <div className="text-sm text-neutral-600">Objective</div>
              <div className="font-semibold text-neutral-900 capitalize">{data.campaignGoals.primaryObjective}</div>
            </div>
            <div className="text-center">
              <Calendar className="w-8 h-8 text-success-500 mx-auto mb-2" />
              <div className="text-sm text-neutral-600">Timeline</div>
              <div className="font-semibold text-neutral-900">{data.campaignGoals.timeline.split(' ')[0]}</div>
            </div>
          </div>
        </motion.div>

        {/* Detailed Sections */}
        <div className="space-y-6 mb-8">
          {sections.map((section, sectionIndex) => {
            const colors = getColorClasses(section.color);
            
            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 + sectionIndex * 0.1 }}
                className={`bg-white border ${colors.border} rounded-2xl p-6`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${colors.bg} rounded-lg`}>
                      <section.icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-neutral-900">{section.title}</h3>
                  </div>
                  <button
                    onClick={() => onEdit(section.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${colors.button}`}
                  >
                    <Edit className="w-4 h-4" />
                    <span className="text-sm font-medium">Edit</span>
                  </button>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="space-y-1">
                      <div className="text-sm font-medium text-neutral-700">{item.label}</div>
                      <div className="text-neutral-900">
                        {item.truncate && item.value.length > 100 
                          ? `${item.value.substring(0, 100)}...`
                          : item.value
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Expected Results */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="bg-gradient-to-br from-success-50 to-primary-50 rounded-2xl p-6 mb-8"
        >
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">
            Expected Campaign Results
          </h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600 mb-1">
                {data.campaignGoals.volumeExpectations.split(' ')[0]}
              </div>
              <div className="text-sm text-neutral-600">Contacts Reached</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-secondary-600 mb-1">
                15-25%
              </div>
              <div className="text-sm text-neutral-600">Response Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-warning-600 mb-1">
                {data.campaignGoals.timeline.split(' ')[0]}
              </div>
              <div className="text-sm text-neutral-600">Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-success-600 mb-1">
                3x
              </div>
              <div className="text-sm text-neutral-600">Higher ROI</div>
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-3 text-neutral-600 hover:text-neutral-900 font-medium transition-colors"
            >
              Back
            </button>

            <button
              onClick={handleSaveDraft}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-3 border border-neutral-300 text-neutral-700 hover:text-neutral-900 hover:border-neutral-400 rounded-xl font-medium transition-all duration-200"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{isSaving ? 'Saving...' : 'Save Draft'}</span>
            </button>
          </div>

          <div className="flex items-center gap-4">
            <button
              className="flex items-center gap-2 px-6 py-3 border border-primary-300 text-primary-700 hover:text-primary-900 hover:border-primary-400 rounded-xl font-medium transition-all duration-200"
            >
              <Download className="w-4 h-4" />
              <span>Export Summary</span>
            </button>

            <motion.button
              onClick={handleLaunch}
              disabled={isLaunching}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                flex items-center gap-3 px-8 py-4 rounded-xl font-semibold transition-all duration-200
                ${isLaunching
                  ? 'bg-neutral-400 text-white cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-glow-primary hover:shadow-strong'
                }
              `}
            >
              {isLaunching ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Launching Campaign...</span>
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5" />
                  <span>Launch Campaign</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Pre-launch checklist */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.7 }}
          className="mt-8 p-4 bg-neutral-50 rounded-xl"
        >
          <h4 className="font-medium text-neutral-900 mb-3">Pre-launch Checklist</h4>
          <div className="grid md:grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2 text-success-600">
              <CheckCircle className="w-4 h-4" />
              <span>Company information verified</span>
            </div>
            <div className="flex items-center gap-2 text-success-600">
              <CheckCircle className="w-4 h-4" />
              <span>Target audience defined</span>
            </div>
            <div className="flex items-center gap-2 text-success-600">
              <CheckCircle className="w-4 h-4" />
              <span>Campaign goals set</span>
            </div>
            <div className="flex items-center gap-2 text-success-600">
              <CheckCircle className="w-4 h-4" />
              <span>Ready for AI optimization</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}; 